import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useDevMode } from "@/contexts/DevModeContext";
import { useMilestones } from "./useMilestones";
import { logEventDirect } from "./useEventLogger";
import { isEphemeralImageUrl, cacheImageIfNeeded } from "@/lib/imageUtils";

export interface ShopResult {
  name: string;
  url: string;
  price: number | null;
  currency: string;
  store: string;
  image: string;
  similarity?: number;
}

export interface ProductVariant {
  color?: string;
  size?: string;
  sku?: string;
  price?: string;
  available?: boolean;
  imageUrl?: string;
}

export interface SavedItemProduct {
  name: string;
  category?: string;
  estimatedPrice?: string;
  brand?: string;
  url?: string;
  position?: number;
  imageUrl?: string;
  image?: string;
  color?: string;
  size?: string;
  material?: string;
  availableSizes?: string[];
  availableColors?: string[];
  shopResults?: ShopResult[];
}

export interface SavedItemPerson {
  identified: boolean;
  name?: string;
  role?: "influencer" | "celebrity" | "model" | "unknown";
}

export interface PhotoLocation {
  latitude: number;
  longitude: number;
  placeName?: string;
}

export interface SavedItem {
  id: string;
  user_id: string;
  name: string | null;
  url: string | null;
  image_url: string | null;
  image_urls: string[];
  note: string | null;
  price: number | null;
  currency: string;
  store: string | null;
  source_type: "url" | "image" | "note" | "barcode";
  in_stock: boolean;
  original_price: number | null;
  price_drop_percent: number | null;
  rating: number | null;
  review_count: number | null;
  ai_analyzed: boolean;
  ai_summary: string | null;
  ai_category: string | null;
  ai_products: SavedItemProduct[];
  ai_tags: string[];
  ai_person: SavedItemPerson | null;
  ai_focal_point: { x: number; y: number } | null;
  ai_variants: ProductVariant[];
  photo_location: PhotoLocation | null;
  platform: string | null;
  creator_name: string | null;
  creator_bio_link: string | null;
  page_id: string | null;
  price_at_save: number | null;
  pinned_at: string | null;
  back_in_stock_at: string | null;
  created_at: string;
  updated_at: string;
  ai_brand_hints?: string[];
}

// AI fields that live in page_ai_data (URL items) or saved_items (non-URL items)
interface AiFields {
  ai_summary?: string | null;
  ai_category?: string | null;
  ai_products?: SavedItemProduct[];
  ai_tags?: string[];
  ai_person?: SavedItemPerson | null;
  ai_focal_point?: { x: number; y: number } | null;
  platform?: string | null;
  creator_name?: string | null;
  creator_bio_link?: string | null;
  review_insights?: any;
  photo_location?: PhotoLocation | null;
}

/**
 * Write AI analysis data to the correct table based on whether the item has a page_id.
 * - URL items (page_id set): write to page_ai_data (shared, deduped)
 * - Image/note items: write to saved_items directly
 * Then mark saved_items.ai_analyzed = true.
 */
async function writeAiData(
  itemId: string,
  pageId: string | null,
  aiFields: AiFields,
) {
  if (pageId) {
    // URL item → shared page_ai_data
    const { photo_location, ...pageAiFields } = aiFields;
    await supabase.from("page_ai_data").upsert(
      {
        page_id: pageId,
        ...pageAiFields,
      } as any,
      { onConflict: "page_id" },
    );
    await supabase
      .from("saved_items")
      .update({ ai_analyzed: true } as any)
      .eq("id", itemId);
  } else {
    // Image/note item → saved_items directly
    const {
      platform,
      creator_name,
      creator_bio_link,
      review_insights,
      ...itemAiFields
    } = aiFields;
    await supabase
      .from("saved_items")
      .update({ ...itemAiFields, ai_analyzed: true } as any)
      .eq("id", itemId);
  }
}

export interface CreateSavedItemInput {
  name?: string;
  url?: string;
  image_url?: string;
  image_urls?: string[];
  note?: string;
  price?: number;
  original_price?: number;
  currency?: string;
  store?: string;
  source_type: "url" | "image" | "note" | "barcode";
  // AI fields — routed to page_ai_data (URL) or saved_items (non-URL)
  ai_summary?: string;
  ai_category?: string;
  ai_products?: SavedItemProduct[];
  ai_tags?: string[];
  ai_person?: {
    identified: boolean;
    name?: string;
    role?: string;
  } | null;
  ai_focal_point?: { x: number; y: number } | null;
  ai_variants?: ProductVariant[];
  photo_location?: PhotoLocation | null;
  platform?: string;
  ai_analyzed?: boolean;
  creator_name?: string | null;
  creator_bio_link?: string | null;
  rating?: number | null;
  review_count?: number | null;
  page_id?: string | null;
  price_at_save?: number | null;
  save_source?: string;
  ai_brand_hints?: string[];
}

// Helper to transform DB row to SavedItem.
// Product data comes from flux_pages.data (URL items) or saved_items columns (non-URL).
// AI data comes from page_ai_data (URL items) or saved_items columns (non-URL).
export function transformSavedItem(row: any): SavedItem {
  const fp = row.flux_pages;
  const fluxData = fp?.data;
  const hasFluxData = row.page_id && fluxData && typeof fluxData === "object";
  const pageAi = fp?.page_ai_data;

  // Extract nested relation objects then remove them from spread
  const { flux_pages, ...rest } = row;

  // AI fields: page_ai_data for URL items, saved_items columns for non-URL
  const aiSource = pageAi || rest;

  // Base: product + AI fields from saved_items columns directly
  const base: SavedItem = {
    ...rest,
    name: rest.name ?? null,
    url: rest.url ?? null,
    image_url: rest.image_url ?? null,
    image_urls: Array.isArray(rest.image_urls) ? rest.image_urls : [],
    price: rest.price ?? null,
    original_price: rest.original_price ?? null,
    currency: rest.currency ?? "USD",
    store: rest.store ?? null,
    rating: rest.rating ?? null,
    review_count: rest.review_count ?? null,
    in_stock: rest.in_stock ?? true,
    // AI fields from page_ai_data (URL items) or saved_items (non-URL items)
    ai_summary: aiSource?.ai_summary ?? null,
    ai_category: aiSource?.ai_category ?? null,
    ai_products: Array.isArray(aiSource?.ai_products)
      ? aiSource.ai_products
      : [],
    ai_tags: Array.isArray(aiSource?.ai_tags) ? aiSource.ai_tags : [],
    ai_person: aiSource?.ai_person || null,
    ai_focal_point: aiSource?.ai_focal_point || null,
    ai_variants: [],
    photo_location: rest.photo_location || null,
    platform: pageAi?.platform ?? rest.platform ?? null,
    creator_name: pageAi?.creator_name ?? rest.creator_name ?? null,
    creator_bio_link: pageAi?.creator_bio_link ?? rest.creator_bio_link ?? null,
    back_in_stock_at: rest.back_in_stock_at || null,
    page_id: rest.page_id || null,
    price_at_save: rest.price_at_save ?? null,
    price_drop_percent: null,
  };

  if (!hasFluxData) {
    // Non-URL items: compute price_drop_percent from saved_items columns
    base.price_drop_percent =
      base.price != null &&
      base.original_price != null &&
      base.original_price > base.price
        ? Math.round(
            ((base.original_price - base.price) / base.original_price) * 100,
          )
        : null;
    return base;
  }

  // URL items: product fields come ONLY from flux_pages.data
  const price =
    fluxData.price != null ? parseFloat(String(fluxData.price)) : null;
  const originalPrice =
    fluxData.originalPrice != null
      ? parseFloat(String(fluxData.originalPrice))
      : null;

  return {
    ...base,
    name: fluxData.title || base.name,
    url: fp.url || base.url,
    image_url:
      fluxData.cdnUrls?.[0] || fluxData.imageUrls?.[0] || base.image_url,
    image_urls:
      Array.isArray(fluxData.imageUrls) && fluxData.imageUrls.length > 0
        ? fluxData.imageUrls
        : base.image_urls,
    price,
    currency: fluxData.currency || base.currency,
    store: fp.tld || fluxData.brand || base.store,
    in_stock:
      fluxData.availability != null
        ? fluxData.availability === "inStock"
        : base.in_stock,
    original_price: originalPrice,
    rating: fluxData.rating ?? base.rating,
    review_count: fluxData.reviewCount ?? base.review_count,
    price_drop_percent:
      price != null &&
      originalPrice != null &&
      originalPrice > price
        ? Math.round(((originalPrice - price) / originalPrice) * 100)
        : null,
  };
}

export function useSavedItems() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isDevMode } = useDevMode();
  const { trackMilestone } = useMilestones();

  // Select columns for the grid. Excludes heavy JSONB (ai_products, ai_person)
  // which are only needed on the detail page via fetchFullItem.
  const GRID_COLUMNS = `
    id, user_id, note, source_type,
    ai_analyzed, pinned_at, back_in_stock_at,
    page_id, price_at_save,
    created_at, updated_at,
    name, url, image_url, image_urls, price, original_price, currency, store,
    rating, review_count, in_stock,
    ai_summary, ai_category, ai_tags,
    ai_focal_point, photo_location, platform, creator_name, creator_bio_link,
    flux_pages:page_id (
      id, url, tld, is_expired, process_status, processed_at, data, category,
      page_ai_data (
        ai_summary, ai_category, ai_tags, ai_focal_point,
        platform, creator_name, creator_bio_link
      )
    )
  `;

  const {
    data: items = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["saved-items", user?.id],
    queryFn: async () => {
      if (!user) return [];

      // First, fetch lightweight columns for grid display
      const { data, error } = await supabase
        .from("saved_items")
        .select(GRID_COLUMNS)
        .eq("user_id", user.id)
        .neq("save_source", "shared")
        .order("pinned_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((row) => transformSavedItem(row));
    },
    enabled: !!user,
    // Keep previous data during refetches to prevent "items briefly empty" flicker
    placeholderData: (previousData) => previousData ?? [],
    // Optimize remounting: don't refetch if data is fresh
    staleTime: 1000 * 60 * 5, // 5 minutes (saved items don't change often)
    gcTime: 1000 * 60 * 30, // 30 minutes (keep in cache longer)
    refetchOnMount: false, // Don't refetch on remount if data exists
    // Use structural sharing to prevent re-renders if data is same
    structuralSharing: true,
  });

  const createItem = useMutation({
    mutationFn: async (input: CreateSavedItemInput) => {
      if (!user) throw new Error("Not authenticated");

      // Safety net: strip Google Shopping redirect URLs that expire and can't be re-scraped
      if (input.url && /google\.com\/.*(?:ibp=oshop|\/url\?)/.test(input.url)) {
        console.warn('[useSavedItems] Stripping Google redirect URL:', input.url?.slice(0, 80));
        input.url = undefined;
      }

      // Cache ephemeral Google Shopping thumbnails before persisting
      if (input.image_url && isEphemeralImageUrl(input.image_url)) {
        input.image_url = await cacheImageIfNeeded(input.image_url);
      }


      // Build insert payload - include product fields directly for non-URL items
      const insertPayload: any = {
        user_id: user.id,
        source_type: input.source_type,
        note: input.note,
        ai_analyzed: input.ai_analyzed ?? false,
        page_id: input.page_id || null,
        price_at_save: input.page_id
          ? (input.price_at_save ?? input.price ?? null)
          : null,
      };

      // Non-URL items: include product fields directly on saved_items
      if (!input.page_id) {
        if (input.name) insertPayload.name = input.name;
        if (input.url) insertPayload.url = input.url;
        if (input.image_url) insertPayload.image_url = input.image_url;
        if (input.image_urls) insertPayload.image_urls = input.image_urls;
        if (input.price !== undefined) insertPayload.price = input.price;
        if (input.original_price !== undefined) insertPayload.original_price = input.original_price;
        if (input.currency) insertPayload.currency = input.currency;
        if (input.store) insertPayload.store = input.store;
        if (input.rating !== undefined) insertPayload.rating = input.rating;
        if (input.review_count !== undefined) insertPayload.review_count = input.review_count;
      }

      const { data, error } = await supabase
        .from("saved_items")
        .insert(insertPayload as any)
        .select(GRID_COLUMNS)
        .single();

      if (error) throw error;

      // Write AI fields to the correct table
      const hasAiData =
        input.ai_summary ||
        input.ai_category ||
        input.ai_tags?.length ||
        input.ai_products?.length;
      if (hasAiData && input.ai_analyzed) {
        const aiFields: AiFields = {
          ai_summary: input.ai_summary,
          ai_category: input.ai_category,
          ai_products: input.ai_products
            ? JSON.parse(JSON.stringify(input.ai_products))
            : [],
          ai_tags: input.ai_tags || [],
          ai_person: input.ai_person
            ? JSON.parse(JSON.stringify(input.ai_person))
            : null,
          ai_focal_point: input.ai_focal_point
            ? JSON.parse(JSON.stringify(input.ai_focal_point))
            : null,
          platform: input.platform,
          creator_name: input.creator_name || null,
          creator_bio_link: input.creator_bio_link || null,
          photo_location: input.photo_location
            ? JSON.parse(JSON.stringify(input.photo_location))
            : null,
        };
        await writeAiData(data.id, input.page_id || null, aiFields);
      }

      // Re-fetch to get joined data
      const { data: refetched } = await supabase
        .from("saved_items")
        .select(GRID_COLUMNS)
        .eq("id", data.id)
        .single();

      return transformSavedItem(refetched || data);
    },
    onSuccess: (data) => {
      // Log event for analytics
      logEventDirect(
        "item_saved",
        "engagement",
        {
          source_type: data.source_type,
          save_source: (data as any).save_source || "app",
          category: data.ai_category,
          store: data.store,
          has_price: !!data.price,
        },
        user?.id,
      );

      // Update persona signals (fire and forget)
      supabase.functions
        .invoke("update-persona-signal", {
          body: {
            type: "save",
            data: {
              name: data.name,
              category: data.ai_category,
              price: data.price,
              store: data.store,
            },
          },
        })
        .catch(console.error);

      // Celebrate first save with confetti and set persistence flag!
      if (items.length === 0) {
        // Set localStorage flag immediately so unlock state persists
        localStorage.setItem("seyu_has_saved", "true");

        import("canvas-confetti").then((confetti) => {
          // First burst - center
          confetti.default({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ["#FFD700", "#FF6B6B", "#4ECDC4", "#A855F7", "#3B82F6"],
          });
          // Second burst - left side
          setTimeout(() => {
            confetti.default({
              particleCount: 50,
              angle: 60,
              spread: 55,
              origin: { x: 0 },
              colors: ["#FFD700", "#FF6B6B", "#4ECDC4"],
            });
          }, 150);
          // Third burst - right side
          setTimeout(() => {
            confetti.default({
              particleCount: 50,
              angle: 120,
              spread: 55,
              origin: { x: 1 },
              colors: ["#A855F7", "#3B82F6", "#FFD700"],
            });
          }, 300);
        });
      }

      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
      // Track first save milestone
      trackMilestone("first_save", { source_type: data.source_type });
    },
  });

  const updateItem = useMutation({
    mutationFn: async ({
      itemId,
      updates,
    }: {
      itemId: string;
      updates: Partial<CreateSavedItemInput>;
    }) => {
      if (!user) throw new Error("Not authenticated");

      const existingItem = items.find((i) => i.id === itemId);
      const pageId = updates.page_id ?? existingItem?.page_id ?? null;

      // saved_items only has core columns now
      const updatePayload: any = {};
      if (updates.note !== undefined) updatePayload.note = updates.note;
      if (updates.ai_analyzed !== undefined)
        updatePayload.ai_analyzed = updates.ai_analyzed;
      if (updates.page_id !== undefined)
        updatePayload.page_id = updates.page_id;
      if (updates.price_at_save !== undefined)
        updatePayload.price_at_save = updates.price_at_save;

      if (Object.keys(updatePayload).length > 0) {
        const { error } = await supabase
          .from("saved_items")
          .update(updatePayload)
          .eq("id", itemId);
        if (error) throw error;
      }

      // Route AI fields to the correct table
      const aiFields: AiFields = {};
      let hasAiUpdates = false;
      if (updates.ai_summary !== undefined) {
        aiFields.ai_summary = updates.ai_summary;
        hasAiUpdates = true;
      }
      if (updates.ai_category !== undefined) {
        aiFields.ai_category = updates.ai_category;
        hasAiUpdates = true;
      }
      if (updates.ai_products !== undefined) {
        aiFields.ai_products = JSON.parse(
          JSON.stringify(updates.ai_products),
        ) as SavedItemProduct[];
        hasAiUpdates = true;
      }
      if (updates.ai_tags !== undefined) {
        aiFields.ai_tags = updates.ai_tags;
        hasAiUpdates = true;
      }
      if (updates.ai_person !== undefined) {
        aiFields.ai_person = updates.ai_person
          ? (JSON.parse(JSON.stringify(updates.ai_person)) as SavedItemPerson)
          : null;
        hasAiUpdates = true;
      }
      if (updates.ai_focal_point !== undefined) {
        aiFields.ai_focal_point = updates.ai_focal_point
          ? JSON.parse(JSON.stringify(updates.ai_focal_point))
          : null;
        hasAiUpdates = true;
      }
      if (updates.platform !== undefined) {
        aiFields.platform = updates.platform;
        hasAiUpdates = true;
      }
      if (updates.creator_name !== undefined) {
        aiFields.creator_name = updates.creator_name;
        hasAiUpdates = true;
      }
      if (updates.creator_bio_link !== undefined) {
        aiFields.creator_bio_link = updates.creator_bio_link;
        hasAiUpdates = true;
      }
      if (updates.photo_location !== undefined) {
        aiFields.photo_location = updates.photo_location;
        hasAiUpdates = true;
      }

      if (hasAiUpdates) {
        if (pageId) {
          // URL item → page_ai_data
          const { photo_location, ...pageAiFields } = aiFields;
          await supabase.from("page_ai_data").upsert(
            {
              page_id: pageId,
              ...pageAiFields,
            } as any,
            { onConflict: "page_id" },
          );
        } else {
          // Non-URL item → saved_items directly
          const {
            platform,
            creator_name,
            creator_bio_link,
            review_insights,
            ...itemAi
          } = aiFields;
          await supabase
            .from("saved_items")
            .update(itemAi as any)
            .eq("id", itemId);
        }
      }

      // Product fields for non-page_id items go to saved_items directly
      if (!pageId) {
        const directFields: Record<string, any> = {};
        if (updates.name !== undefined) directFields.name = updates.name;
        if (updates.url !== undefined) directFields.url = updates.url;
        if (updates.image_url !== undefined)
          directFields.image_url = updates.image_url;
        if (updates.image_urls !== undefined)
          directFields.image_urls = updates.image_urls;
        if (updates.price !== undefined) directFields.price = updates.price;
        if (updates.original_price !== undefined)
          directFields.original_price = updates.original_price;
        if (updates.currency !== undefined)
          directFields.currency = updates.currency;
        if (updates.store !== undefined) directFields.store = updates.store;
        if (updates.rating !== undefined) directFields.rating = updates.rating;
        if (updates.review_count !== undefined)
          directFields.review_count = updates.review_count;

        if (Object.keys(directFields).length > 0) {
          await supabase
            .from("saved_items")
            .update(directFields as any)
            .eq("id", itemId);
        }
      }

      // Re-fetch to get joined data
      const { data: refetched, error: refetchErr } = await supabase
        .from("saved_items")
        .select(GRID_COLUMNS)
        .eq("id", itemId)
        .single();

      if (refetchErr) throw refetchErr;
      return transformSavedItem(refetched);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
    },
  });

  const deleteItem = useMutation({
    mutationFn: async (itemId: string) => {
      if (!user) throw new Error("Not authenticated");

      // Capture item metadata before deletion for regret signal tracking
      const itemToDelete = items.find((i) => i.id === itemId);

      const { error } = await supabase
        .from("saved_items")
        .delete()
        .eq("id", itemId);

      if (error) throw error;

      // Log regret signal: track save-then-delete patterns
      if (itemToDelete) {
        const savedAt = new Date(itemToDelete.created_at).getTime();
        const now = Date.now();
        const daysSinceSave = Math.round(
          (now - savedAt) / (1000 * 60 * 60 * 24),
        );

        logEventDirect(
          "item_deleted",
          "engagement",
          {
            item_id: itemId,
            item_name: itemToDelete.name || null,
            category: itemToDelete.ai_category || null,
            store: itemToDelete.store || null,
            price: itemToDelete.price,
            days_since_save: daysSinceSave,
            is_regret: daysSinceSave <= 7, // Deleted within a week = likely regret
          },
          user.id,
        );
      }
    },
    onMutate: async (itemId: string) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["saved-items", user?.id] });

      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData(["saved-items", user?.id]);

      // Optimistically remove the item from cache
      queryClient.setQueryData(["saved-items", user?.id], (old: any) => {
        if (!old) return old;
        // Handle both array and paginated shapes
        if (Array.isArray(old)) {
          return old.filter((item: any) => item.id !== itemId);
        }
        return old;
      });

      return { previous };
    },
    onError: (_err, _itemId, context) => {
      // Roll back on error
      if (context?.previous) {
        queryClient.setQueryData(["saved-items", user?.id], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["collection-items"] });
    },
  });

  const deleteMultipleItems = useMutation({
    mutationFn: async (itemIds: string[]) => {
      if (!user) throw new Error("Not authenticated");
      if (itemIds.length === 0) return;

      const { error } = await supabase
        .from("saved_items")
        .delete()
        .in("id", itemIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["collection-items"] });
    },
  });

  const togglePin = useMutation({
    mutationFn: async ({
      itemId,
      isPinned,
    }: {
      itemId: string;
      isPinned: boolean;
    }) => {
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("saved_items")
        .update({
          pinned_at: isPinned ? null : new Date().toISOString(),
        } as any)
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
    },
  });

  const clearBackInStock = useMutation({
    mutationFn: async (itemId: string) => {
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("saved_items")
        .update({ back_in_stock_at: null } as any)
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
    },
  });

  const refreshItem = useMutation({
    mutationFn: async ({ itemId, url }: { itemId: string; url: string }) => {
      if (!user) throw new Error("Not authenticated");

      // Call extract-url to get fresh data (also updates flux_pages)
      const { data: extractedData, error: extractError } =
        await supabase.functions.invoke("extract-url", {
          body: { url },
        });

      if (extractError) throw extractError;
      if (!extractedData) throw new Error("No data returned from extraction");

      const pageId = extractedData.data?.fluxPageId || null;
      let coreUpdate: any = { ai_analyzed: true };

      // If extract-url returned a fluxPageId, set page_id on saved_items
      if (pageId) {
        coreUpdate.page_id = pageId;
      }

      // Product data for non-page_id items goes to saved_items directly
      if (!pageId) {
        const directFields: Record<string, any> = {};
        if (extractedData.name) directFields.name = extractedData.name;
        if (extractedData.price !== undefined)
          directFields.price = extractedData.price;
        if (extractedData.originalPrice !== undefined)
          directFields.original_price = extractedData.originalPrice;
        if (extractedData.currency)
          directFields.currency = extractedData.currency;
        if (extractedData.image) directFields.image_url = extractedData.image;
        if (extractedData.images?.length > 0)
          directFields.image_urls = extractedData.images;
        if (extractedData.store) directFields.store = extractedData.store;
        if (extractedData.inStock !== undefined)
          directFields.in_stock = extractedData.inStock;

        if (Object.keys(directFields).length > 0) {
          coreUpdate = { ...coreUpdate, ...directFields };
        }
      }

      // AI fields go to page_ai_data (URL items) or saved_items columns (image items)
      const aiFields: AiFields = {};
      if (extractedData.summary) aiFields.ai_summary = extractedData.summary;
      if (extractedData.category) aiFields.ai_category = extractedData.category;
      if (extractedData.products)
        aiFields.ai_products = JSON.parse(
          JSON.stringify(extractedData.products),
        );
      if (extractedData.tags) aiFields.ai_tags = extractedData.tags;
      if (extractedData.person)
        aiFields.ai_person = JSON.parse(JSON.stringify(extractedData.person));
      if (extractedData.platform) aiFields.platform = extractedData.platform;
      if (extractedData.creatorName)
        aiFields.creator_name = extractedData.creatorName;
      if (extractedData.creatorBioLink)
        aiFields.creator_bio_link = extractedData.creatorBioLink;

      await writeAiData(itemId, pageId, aiFields);

      // Update core saved_items fields
      const { data, error } = await supabase
        .from("saved_items")
        .update(coreUpdate)
        .eq("id", itemId)
        .select(GRID_COLUMNS)
        .single();

      if (error) throw error;
      return transformSavedItem(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items", user?.id] });
    },
  });

  const uploadImage = async (file: File | Blob): Promise<string> => {
    if (!user) throw new Error("Not authenticated");

    const fileExt = "jpg";
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("saved-images")
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const {
      data: { publicUrl },
    } = supabase.storage.from("saved-images").getPublicUrl(fileName);

    return publicUrl;
  };

  // Fetch full item details including heavy JSONB columns (ai_products from page_ai_data)
  // Use this for detail pages where all data is needed
  const fetchFullItem = async (itemId: string): Promise<SavedItem | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from("saved_items")
      .select(
        `
        id, user_id, note, source_type,
        ai_analyzed, pinned_at, back_in_stock_at,
        page_id, price_at_save, created_at, updated_at,
        name, url, image_url, image_urls, price, original_price, currency, store,
        rating, review_count, in_stock,
        ai_summary, ai_category, ai_products, ai_tags, ai_person,
        ai_focal_point, photo_location, platform, creator_name, creator_bio_link,
        flux_pages:page_id (
          id, url, tld, is_expired, process_status, processed_at, data, category,
          page_ai_data (
            ai_summary, ai_category, ai_products, ai_tags, ai_person,
            ai_focal_point, platform, creator_name, creator_bio_link, review_insights
          )
        )
      `,
      )
      .eq("id", itemId)
      .single();

    if (error || !data) return null;
    return transformSavedItem(data);
  };

  return {
    items: isDevMode ? [] : items,
    isLoading,
    isFetching,
    error,
    refetch,
    createItem,
    updateItem,
    deleteItem,
    deleteMultipleItems,
    togglePin,
    clearBackInStock,
    refreshItem,
    uploadImage,
    fetchFullItem,
  };
}
